<template>
    <div>
        <h4>{{ $t('wanderManagement.schedulersTab.timeWindows') }}</h4>
        <div class="time-windows">
            <DaySelector
                :current-week-day="currentWeekDay"
                :day-is-disabled="() => false"
                @selectDay="setActiveWeekDay"
            />
            <div class="time-windows-wrapper">
                <div v-for="i in [0, 1]" :key="i">
                    <TimeWindow
                        :options-start="getOptionsStart(i)"
                        :options-end="getOptionsEnd(i)"
                        :value="timeValues[i] || {}"
                        @update="updateTimeWindow($event, timeValues[i] ? timeValues[i].id : null, i)"
                    />
                    <button
                        v-show="timeValues[i]"
                        v-on-clickaway="hideDaySelector"
                        class="icon-copy"
                        :disabled="copyButtonDisabled(timeValues[i])"
                        @click.stop="selectIntervalToCopy(timeValues[i], i)"
                    ></button>
                    <button
                        v-show="timeValues[i]"
                        class="icon-trash"
                        @click.stop="deleteTimeInterval(timeValues[i])"
                    ></button>
                </div>
                <div
                    v-show="showDaySelector"
                    class="day-selector-wrapper"
                    :style="{ top: intervalToCopyTop }"
                >
                    <p>{{ $t('wanderManagement.schedulersTab.copyInterval') }}</p>
                    <DaySelector
                        :current-week-day="currentWeekDay"
                        :day-is-disabled="intervalIsRefusedForCopy"
                        @selectDay="copyInterval"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script type="text/babel">
import TimeWindow from '../../../common/TimeWindow';
import DaySelector from './DaySelector';
import { mixin as clickaway } from 'vue-clickaway';
import { daysOfWeek, toMinutes } from './timeWindowHelper';

export default {
    components: {
        DaySelector,
        TimeWindow,
    },

    mixins: [clickaway],

    props: {
        value: {
            type: Array,
            default: () => [],
        },
    },

    data() {
        return {
            weekDays: daysOfWeek,
            currentWeekDay: 0,
            showDaySelector: false,
            intervalToCopy: {},
            intervalToCopyTop: '36%',
        };
    },

    computed: {
        /**
         * Filters only values for selected day of the week, maps to object with {from, to, id, windowKey} properties.
         * If element has windowKey property, it should be placed on [windowKey] row.
         */
        timeValues() {
            let res = [];
            let values = this.value.length
                ? this.value
                      .filter(v => v.day_of_week === this.currentWeekDay)
                      .map(v => ({
                          from: (v.begin_time || '').slice(0, 5),
                          to: (v.end_time || '').slice(0, 5),
                          id: v.time_window_scheduler_id,
                          windowKey: v.windowKey,
                      }))
                : [];
            for (let v of values) {
                if (v && Number.isInteger(v.windowKey)) {
                    res[v.windowKey] = v;
                } else res.push(v);
            }
            return res;
        },
    },

    watch: {
        $route: {
            handler() {
                this.currentWeekDay = 0;
            },
        },
    },

    methods: {
        /**
         * Creates and pushes new interval with the same from/to parameters to target day
         * @param dayTarget
         */
        copyInterval(dayTarget) {
            let newTime = this.value.slice(0);
            let windowKey = newTime.findIndex(i => i.day_of_week === dayTarget) !== -1 ? 1 : 0;
            let timeWindow = {
                day_of_week: dayTarget,
                begin_time: this.intervalToCopy.from,
                end_time: this.intervalToCopy.to,
                windowKey,
            };
            newTime.push(timeWindow);
            this.$emit('update', newTime);
        },

        copyButtonDisabled(interval) {
            return !!interval && (!interval.from || !interval.to);
        },

        /**
         * Deletes the interval from value (emits event with new time value)
         * @param interval
         */
        deleteTimeInterval(interval) {
            let newTime = this.value.slice(0);
            if (interval.id !== undefined) {
                let index = newTime.findIndex(
                    i => i.time_window_scheduler_id === interval.id && i.day_of_week === this.currentWeekDay
                );
                if (index !== -1) {
                    newTime.splice(index, 1);
                    this.$emit('update', newTime);
                }
            } else if (interval.windowKey !== undefined) {
                let index = newTime.findIndex(
                    i => i.windowKey === interval.windowKey && i.day_of_week === this.currentWeekDay
                );
                if (index !== -1) {
                    newTime.splice(index, 1);
                    this.$emit('update', newTime);
                }
            }
        },

        /**
         * Returns minimum valid index of 15 minutes interval for time window
         * @param index
         * @return {number}
         */
        getOptionsStart(index) {
            if (index === 0) {
                return 0;
            } else if (index === 1) {
                let minutes = this.timeValues[0]
                    ? this.timeValues[0].to
                        ? this.timeValues[0].to.replace('00:00', '24:00').split(':')
                        : this.timeValues[0].from.split(':')
                    : [];

                return minutes.length === 2 ? (minutes[0] * 60 + Number(minutes[1])) / 15 + 1 : 0;
            }
        },

        /**
         * Returns maximum valid index of 15 minutes interval for time window
         * @param index
         * @return {number}
         */
        getOptionsEnd(index) {
            if (index === 1) {
                return 96;
            } else if (index === 0) {
                let minutes = this.timeValues[1] ? this.timeValues[1].from.split(':') : [];
                return minutes.length === 2 ? (minutes[0] * 60 + Number(minutes[1])) / 15 : 96;
            }
        },

        /**
         * Hides day selector in copy interval option
         */
        hideDaySelector() {
            this.showDaySelector = false;
        },

        /**
         * Checks if target day has any interval with interfering time
         */
        dayHasInterferingWindows(day) {
            let targetDayValue = this.value.filter(v => v.day_of_week === day);

            return targetDayValue.length
                ? targetDayValue.some(
                      v =>
                          toMinutes(v.begin_time) < toMinutes(this.intervalToCopy.to) &&
                          toMinutes(v.end_time) > toMinutes(this.intervalToCopy.from)
                  )
                : false;
        },

        intervalIsRefusedForCopy(day) {
            return this.intervalIsFull(day) || this.dayHasInterferingWindows(day);
        },

        /**
         * Check if day contains more then 1 interval
         * @param day
         * @return {boolean}
         */
        intervalIsFull(day) {
            return this.value.filter(v => v.day_of_week === day).length > 1;
        },

        /**
         * Sets data that can be copied later
         * @param interval
         * @param intervalIndex
         */
        selectIntervalToCopy(interval, intervalIndex) {
            this.showDaySelector = true;
            this.intervalToCopy = interval;
            this.intervalToCopyTop = intervalIndex === 0 ? '39%' : '89%';
        },

        /**
         * Sets selected dy as active to show time windows for this day
         * @param index
         */
        setActiveWeekDay(index) {
            this.currentWeekDay = index;
        },

        /**
         * Updates time value with edited or new time interval
         * @param event
         * @param windowId
         * @param windowKey
         */
        updateTimeWindow(event, windowId, windowKey) {
            let newTime = this.value.slice(0);
            let prop = event.option === 'from' ? 'begin_time' : 'end_time';
            let index = windowId ? this.value.findIndex(i => i.time_window_scheduler_id === windowId) : null;
            if (index !== null && index !== -1) {
                let newTimeWindow = Object.assign({}, newTime[index], { [prop]: event.value });
                newTime[index] = newTimeWindow;
                this.$emit('update', newTime);
            } else {
                index = this.value.findIndex(
                    i =>
                        !i.time_window_scheduler_id &&
                        i.windowKey === windowKey &&
                        i.day_of_week === this.currentWeekDay
                );

                if (index > -1) {
                    let newTimeWindow = Object.assign({}, newTime[index], { [prop]: event.value });
                    newTime[index] = newTimeWindow;
                } else {
                    newTime.push({
                        [prop]: event.value,
                        day_of_week: this.currentWeekDay,
                        windowKey,
                    });
                }
                this.$emit('update', newTime);
            }
        },
    },
};
</script>

<style scoped></style>
